import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { generateRandomString } from "utils/helper";
import { setIsLoading, setReportOvertimeList } from "../../slices/reportSlice";
import * as services from "../../services/reportApi";
import useOptions from "../../../masterData/hooks/useOptions";

function useOvertimeReport() {
    const { i18n } = useTranslation();
    const dispatch = useDispatch();
    const [showReport, setShowReport] = useState(false);
    const [search, setSearch] = useState({});
    const currentCompany = useSelector((state) => state.company.currentCompany);
    const currentEmployee = useSelector((state) => state.company.currentEmployee);
    const [positionOptions, setPositionOptions] = useState([]);
    const [departmentListOption, setDepartmentListOption] = useState([]);
    const { employmentTypeOptions, getEmploymentTypeOptions } = useOptions();
    const [selectedDates, setSelectedDates] = useState(null);
    const [pagination, setPagination] = useState({ page: 0, currentPage: 0, size: 10 });
    const [isModalOpen, setIsModalOpen] = useState(false);
    const rePortTableOvertime = useSelector((state) => state.report.rePortTableOvertime || []);

    const showModal = () => {
        setIsModalOpen(true);
    };

    const modifiedPositionOptions = useMemo(() => {
        const modifiedOptions = positionOptions.map((opt) => ({
            ...opt,
            id: opt?.positionId,
            value: opt?.positionId,
            nameEn: opt?.positionNameEn,
            name: opt?.positionName,
            label: i18n.language === "en" ? opt?.positionNameEn : opt?.positionName,
        }));
        return modifiedOptions?.sort((a, b) =>
            a?.label?.localeCompare(b?.label, i18n?.language === "en" ? "en" : "th", { sensitivity: "base" }));
    }, [i18n.language, positionOptions]);

    const modifiedDepartmentOption = useMemo(() => {
        const modifiedOptions = departmentListOption?.map((opt) => ({
            ...opt,
            value: opt?.id,
            label: i18n.language === "en" ? opt?.nameEn : opt?.name,
        }));

        return modifiedOptions?.sort((a, b) =>
            a?.label?.localeCompare(b?.label, i18n?.language === "en" ? "en" : "th", { sensitivity: "base" }));
    }, [i18n.language, departmentListOption]);

    const getPosition = useCallback(async () => {
        if (typeof currentCompany !== "object") {
            try {
                dispatch(setIsLoading(true));
                const values = {
                    requestId: generateRandomString(),
                    companyId: currentCompany,
                };
                const response = await services.getPosition(values);
                setPositionOptions(response.data);
            } catch (error) {
                // alert({ type: "error", resultObject: error });
            } finally {
                dispatch(setIsLoading(false));
            }
        }
    }, [currentCompany, dispatch]);

    const getDepartment = useCallback(async () => {
        if (!currentCompany) return null;
        try {
            dispatch(setIsLoading(true));
            const response = await services.getDepartmentOptions({
                requestId: generateRandomString(),
                companyId: currentCompany,
            });
            dispatch(setDepartmentListOption(response.data));
        } catch (error) {
            // alert({ type: "error", resultObject: error });
        } finally {
            dispatch(setIsLoading(false));
        }
    }, [currentCompany, dispatch]);

    const handleDateChange = (dates) => {
        setSelectedDates(dates);
    };

    function toParams(params = {}) {
        const { pagination = {}, sortBy = [] } = params;
        const modifiedParams = {
            pagination: {
                page: pagination?.page,
                size: pagination?.pageSize,
            },
            sortBy: sortBy
                .filter((s) => s.order !== undefined)
                .map((s) => ({
                    direction: s.order === "descend" ? "desc" : "asc",
                    property: s.field,
                })),
        };
        return modifiedParams;
    }

    const getOvertimeTableReport = useCallback(
        async (params = {}) => {
            try {
                dispatch(setIsLoading(true));
                const response = await services.getOvertimeReport({
                    requestId: generateRandomString(),
                    employeeId: currentEmployee,
                    companyId: currentCompany,
                    ...params,
                    informationType: 1,
                    ...params.pagination,
                });

                dispatch(setReportOvertimeList(response?.data?.page?.content));

                setPagination({
                    ...pagination,
                    current: response?.data?.page?.pageable?.pageNumber + 1,
                    pageNo: response?.data?.page?.pageable?.pageNumber + 1,
                    size: response?.data?.page?.pageable?.pageSize,
                    total: response?.data?.page?.totalElements,
                });
            } catch (error) {
                console.error("Error fetching report history:", error);
            } finally {
                dispatch(setIsLoading(false));
            }
        },
        [currentCompany, dispatch, pagination]
    );

    const exportOvertimeReport = useCallback(
        async (params = {}) => {
            console.log("params", search);
            try {
                dispatch(setIsLoading(true));
                services.ExportOvertimeReportExcel({
                    requestId: generateRandomString(),
                    companyId: currentCompany,
                    ...params,
                    ...params.pagination,
                    ...search,
                    ...pagination,
                    isReport: "Y",
                    sortBy: params.sortBy
                }).then(res => {
                    const url = window.URL.createObjectURL(res?.data);
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', "overtime-report.xlsx");
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                });
            } catch (error) {
                console.error("Error fetching report history:", error);
            } finally {
                dispatch(setIsLoading(false));
            }
        },
        [currentCompany, dispatch, search]
    );

    const onChange = useCallback(
        (tablePagination, tableFilters, tableSorter, tableExtra) => {
            console.log('table', tablePagination);

            const modifiedSorter = Array.isArray(tableSorter)
                ? [...tableSorter]
                : [tableSorter];
            const modifiedParams = toParams({
                pagination: {
                    page: tablePagination.current - 1,
                    pageSize: tablePagination.pageSize,
                    current: tablePagination.current - 1,
                },
                sortBy: modifiedSorter,
            });
            setPagination({ ...pagination, ...modifiedParams?.pagination })
            getOvertimeTableReport({ ...modifiedParams, request: pagination.request, ...search });
        },
        [getOvertimeTableReport, pagination]
    );

    const handleSearch = (values) => {
        setShowReport(true)
        setSearch({})
        try {
            setSearch({
                ...values,
                startDate: values?.startToEndDate?.[0] ? values?.startToEndDate?.[0]?.format('YYYY-MM-DD') : null,
                endDate: values?.startToEndDate?.[1] ? values?.startToEndDate?.[1]?.format('YYYY-MM-DD') : null
            })
            getOvertimeTableReport({
                ...values,
                startDate: values?.startToEndDate?.[0] ? values?.startToEndDate?.[0]?.format('YYYY-MM-DD') : null,
                endDate: values?.startToEndDate?.[1] ? values?.startToEndDate?.[1]?.format('YYYY-MM-DD') : null,
                ...pagination,
            })
        } catch (error) {
            console.log("error", error);

        }
    };

    useEffect(() => {
        if (currentCompany) {
            getPosition();
            getDepartment();
            getEmploymentTypeOptions({ companyId: currentCompany });
        }
    }, [currentCompany, getPosition, getDepartment, getEmploymentTypeOptions])

    return {
        handleDateChange,
        handleSearch,
        setIsModalOpen,
        showModal,
        exportOvertimeReport,
        onChange,
        positionOptions: modifiedPositionOptions,
        departmentOptions: modifiedDepartmentOption,
        employmentTypeOptions,
        showReport,
        isModalOpen,
        rePortTableOvertime,
        pagination
    }
}

export default useOvertimeReport;